// data-table-core.js — logica PURA del componente condiviso DataTable (F2.3b,
// primo mattone del design system in client/src/ui/).
// File PURO (niente JSX, niente DOM): è QUESTO che il contract test
// server/src/lib/data-table.test.js esercita sotto node --test — il JSX
// (DataTable.jsx) resta un guscio di resa che DELEGA qui ogni decisione.

// Selezione dello stato da mostrare. Precedenza DICHIARATA (e testata):
//  - righe presenti ⇒ 'data' SEMPRE (reload in corso o fallito NON svuota la
//    lista: il chiamante comunica l'errore col suo banner — parità col v1
//    "le liste tengono i dati precedenti");
//  - senza righe: error ('error', messaggio + Riprova) › loading ('loading',
//    spinner) › 'empty' (messaggio dedicato).
export function selectTableState(s) {
  const rows = (s && s.rows) || [];
  if (rows.length) return 'data';
  if (s && s.error) return 'error';
  if (s && s.loading) return 'loading';
  return 'empty';
}

// Chiave di riga: campo (stringa), funzione, fallback indice — mai undefined
// (React perderebbe l'identità delle righe in silenzio).
export function rowKeyOf(row, rowKey, index) {
  if (typeof rowKey === 'function') {
    const k = row == null ? null : rowKey(row);
    if (k != null) return String(k);
  } else if (typeof rowKey === 'string' && row && row[rowKey] != null) {
    return String(row[rowKey]);
  }
  return 'row-' + index;
}

// Prossimo ordinamento al click sull'intestazione: nuova chiave = asc,
// stessa chiave = toggle asc↔desc.
export function nextSort(current, key) {
  if (current && current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

// --- Filtri per colonna (F2.3c, semantica del makeDataTable v1 proc-v0.js:10814) ---
// La query di UNA colonna: la VIRGOLA separa gruppi in OPPURE, gli spazi dentro
// ogni gruppo sono token in E. Es: "metalli, viti" ⇒ (metalli) OPPURE (viti);
// "acciaio inox" ⇒ acciaio E inox. Tutto case-insensitive, match a sottostringa.
export function parseFilterQuery(q) {
  return String(q || '')
    .split(',')
    .map((g) => g.trim().toLowerCase().split(/\s+/).filter(Boolean))
    .filter((g) => g.length);
}

// true se il testo soddisfa la query (gruppi OR, token AND). Query vuota = passa.
export function matchesFilterQuery(text, groups) {
  if (!groups || !groups.length) return true;
  const lo = String(text == null ? '' : text).toLowerCase();
  return groups.some((tokens) => tokens.every((t) => lo.indexOf(t) !== -1));
}

// Testo di match della cella: filterText della colonna (nel v1 era
// stripHtml(render(row))) o il valore piano del campo.
export function filterTextOf(row, col) {
  if (col && typeof col.filterText === 'function') return String(col.filterText(row) == null ? '' : col.filterText(row));
  const v = row == null ? undefined : row[col && col.key];
  return v == null ? '' : String(v);
}

// Applica TUTTI i filtri per colonna (E tra colonne, come il v1). Input mai mutato.
export function filterRows(rows, filters, columns) {
  let list = rows || [];
  const f = filters || {};
  for (const col of columns || []) {
    if (!col) continue;
    const groups = parseFilterQuery(f[col.key]);
    if (!groups.length) continue;
    list = list.filter((row) => matchesFilterQuery(filterTextOf(row, col), groups));
  }
  return list;
}

// Token piatti di una query (per l'evidenziazione: unione dei gruppi, come il v1).
export function collectTokens(q) {
  const out = [];
  for (const g of parseFilterQuery(q)) for (const t of g) if (out.indexOf(t) === -1) out.push(t);
  return out;
}

// Spezza un testo nei segmenti da evidenziare: [{text, hit}] — i token più LUNGHI
// vincono sui più corti (parità v1:10828), match case-insensitive, testo originale
// preservato nei segmenti. Nessun token ⇒ un solo segmento piano.
export function splitByTokens(text, tokens) {
  const s = String(text == null ? '' : text);
  const toks = (tokens || []).filter(Boolean).slice().sort((a, b) => b.length - a.length);
  if (!s || !toks.length) return [{ text: s, hit: false }];
  const lo = s.toLowerCase();
  const out = [];
  let i = 0;
  while (i < s.length) {
    let best = -1;
    let bestTok = null;
    for (const t of toks) {
      const at = lo.indexOf(t, i);
      if (at === -1) continue;
      if (best === -1 || at < best || (at === best && t.length > bestTok.length)) { best = at; bestTok = t; }
    }
    if (best === -1) { out.push({ text: s.slice(i), hit: false }); break; }
    if (best > i) out.push({ text: s.slice(i, best), hit: false });
    out.push({ text: s.slice(best, best + bestTok.length), hit: true });
    i = best + bestTok.length;
  }
  return out.length ? out : [{ text: '', hit: false }];
}

// Ordinamento: STABILE (pari merito = ordine di arrivo), numeri numerici,
// stringhe localeCompare 'it' (numeric: '2' < '10'), null/undefined SEMPRE in
// coda qualunque direzione, input MAI mutato. La colonna può dichiarare
// sortValue(row) per ordinare su un valore derivato.
export function sortRows(rows, sort, columns) {
  const list = rows || [];
  if (!sort || !sort.key) return list;
  const col = (columns || []).find((c) => c && c.key === sort.key) || null;
  const val = (row) => (col && typeof col.sortValue === 'function')
    ? col.sortValue(row)
    : (row == null ? undefined : row[sort.key]);
  const dir = sort.dir === 'desc' ? -1 : 1;
  return list
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const va = val(a.row);
      const vb = val(b.row);
      const na = va == null;
      const nb = vb == null;
      if (na && nb) return a.i - b.i;
      if (na) return 1;  // null in coda, sempre
      if (nb) return -1;
      let c;
      if (typeof va === 'number' && typeof vb === 'number') c = va - vb;
      else c = String(va).localeCompare(String(vb), 'it', { numeric: true, sensitivity: 'base' });
      if (c === 0) return a.i - b.i; // stabilità
      return c * dir;
    })
    .map((x) => x.row);
}
